import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, IconButton, Stack, TextField,
  LinearProgress, Alert, Tabs, Tab, CircularProgress, Tooltip,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../api';

// ─── IndexedDB helpers for crash recovery ────────────────────────────────────
const IDB_NAME = 'lifehub_rec';
const IDB_STORE = 'chunks';

function openIDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = e => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(IDB_STORE))
        db.createObjectStore(IDB_STORE, { keyPath: 'key' });
    };
    req.onsuccess = e => resolve(e.target.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(record) {
  const db = await openIDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).put(record);
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
}

async function idbGetBySession(sessionId) {
  const db = await openIDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readonly');
    const req = tx.objectStore(IDB_STORE).getAll();
    req.onsuccess = () =>
      resolve(req.result
        .filter(r => r.sessionId === sessionId)
        .sort((a, b) => a.index - b.index)
        .map(r => r.data));
    req.onerror = () => reject(req.error);
  });
}

async function idbClearSession(sessionId) {
  const db = await openIDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    const store = tx.objectStore(IDB_STORE);
    const req = store.getAll();
    req.onsuccess = () => req.result
      .filter(r => r.sessionId === sessionId)
      .forEach(r => store.delete(r.key));
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
}

const SESSION_KEY = 'recording_active_session';

function saveSessionMeta(meta) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(meta));
}
function loadSessionMeta() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || 'null'); } catch { return null; }
}
function clearSessionMeta() {
  localStorage.removeItem(SESSION_KEY);
}

function fmtDuration(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// ─── RecordingModal ───────────────────────────────────────────────────────────
export default function RecordingModal({ open, onClose, trackerNodeId, trackerNodeTitle, onJobStarted }) {
  const [tab, setTab] = useState(0); // 0=upload, 1=live
  const [phase, setPhase] = useState('capture'); // 'capture' | 'name' | 'submitting'

  // Upload tab state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedKey, setUploadedKey] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Live record state
  const [recState, setRecState] = useState('idle'); // idle | recording | paused | done
  const [duration, setDuration] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recUploadedKey, setRecUploadedKey] = useState(null);
  const [recUploading, setRecUploading] = useState(false);

  // Recovery
  const [recoveredSession, setRecoveredSession] = useState(null);

  // Naming
  const [recordingName, setRecordingName] = useState('');
  const [submitError, setSubmitError] = useState('');

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const chunkIndexRef = useRef(0);
  const sessionIdRef = useRef(null);
  const timerRef = useRef(null);
  const analyserRef = useRef(null);
  const animRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setTab(0); setPhase('capture');
      setUploadFile(null); setUploadProgress(0); setUploadedKey(null); setUploading(false);
      setRecState('idle'); setDuration(0); setRecordedBlob(null); setRecUploadedKey(null); setRecUploading(false);
      setRecordingName('');
      setSubmitError('');
      chunksRef.current = []; chunkIndexRef.current = 0;
      // Check for recovered session
      const meta = loadSessionMeta();
      if (meta) setRecoveredSession(meta);
    } else {
      stopAnimation();
    }
  }, [open]);

  // Default recording name
  useEffect(() => {
    if (phase === 'name') {
      const d = new Date();
      const dateStr = d.toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '').toLowerCase();
      const base = trackerNodeTitle
        ? `${dateStr}-${trackerNodeTitle.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}-meeting`
        : `${dateStr}-meeting`;
      setRecordingName(base);
    }
  }, [phase, trackerNodeTitle]);

  function stopAnimation() {
    if (animRef.current) cancelAnimationFrame(animRef.current);
  }

  function drawWaveform() {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas || !analyser) return;
    const ctx = canvas.getContext('2d');
    const data = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(data);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const bw = (canvas.width / data.length) * 2.5;
    let x = 0;
    for (let i = 0; i < data.length; i++) {
      const h = (data[i] / 255) * canvas.height;
      ctx.fillStyle = `rgba(99,102,241,${0.4 + (data[i] / 255) * 0.6})`;
      ctx.fillRect(x, canvas.height - h, bw - 1, h);
      x += bw;
    }
    animRef.current = requestAnimationFrame(drawWaveform);
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const audioCtx = new AudioContext();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 128;
      source.connect(analyser);
      analyserRef.current = analyser;

      const sessionId = Date.now().toString();
      sessionIdRef.current = sessionId;
      chunksRef.current = [];
      chunkIndexRef.current = 0;
      saveSessionMeta({ session_id: sessionId, tracker_node_id: trackerNodeId, tracker_node_title: trackerNodeTitle, started_at: new Date().toISOString() });

      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = mr;

      mr.ondataavailable = async (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
          // Persist chunk to IDB for crash recovery
          const idx = chunkIndexRef.current++;
          const buf = await e.data.arrayBuffer();
          await idbPut({ key: `${sessionId}_${idx}`, sessionId, index: idx, data: buf }).catch(() => {});
        }
      };

      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm;codecs=opus' });
        setRecordedBlob(blob);
        setRecState('done');
        stopAnimation();
        clearSessionMeta();
        if (sessionIdRef.current) idbClearSession(sessionIdRef.current).catch(() => {});
        stream.getTracks().forEach(t => t.stop());
      };

      mr.start(10000); // chunk every 10s
      setRecState('recording');
      setDuration(0);
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
      drawWaveform();
    } catch (e) {
      setSubmitError('Could not access microphone: ' + e.message);
    }
  }

  function pauseRecording() {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.pause();
      clearInterval(timerRef.current);
      stopAnimation();
      setRecState('paused');
    }
  }

  function resumeRecording() {
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume();
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
      drawWaveform();
      setRecState('recording');
    }
  }

  function stopRecording() {
    clearInterval(timerRef.current);
    mediaRecorderRef.current?.stop();
  }

  async function recoverSession() {
    const meta = recoveredSession;
    if (!meta) return;
    try {
      const bufs = await idbGetBySession(meta.session_id);
      if (bufs.length === 0) { clearSessionMeta(); setRecoveredSession(null); return; }
      const blob = new Blob(bufs.map(b => new Uint8Array(b)), { type: 'audio/webm;codecs=opus' });
      setRecordedBlob(blob);
      setRecState('done');
      setTab(1);
      clearSessionMeta();
      await idbClearSession(meta.session_id).catch(() => {});
      setRecoveredSession(null);
    } catch {
      setRecoveredSession(null);
    }
  }

  const multipartHeaders = { headers: { 'Content-Type': undefined } };

  // Upload the recorded blob to backend
  async function uploadRecordedAudio() {
    if (!recordedBlob) return;
    setRecUploading(true);
    try {
      const fd = new FormData();
      fd.append('audio', new File([recordedBlob], 'recording.webm', { type: 'audio/webm' }));
      const res = await api.post('/api/meeting-audio', fd, multipartHeaders);
      setRecUploadedKey(res.data.audio_key);
      setPhase('name');
    } catch (e) {
      setSubmitError('Upload failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setRecUploading(false);
    }
  }

  // Handle file upload tab
  async function handleFileSelect(file) {
    if (!file) return;
    setUploadFile(file);
    setUploading(true);
    setUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append('audio', file);
      const res = await api.post('/api/meeting-audio', fd, {
        ...multipartHeaders,
        onUploadProgress: e => setUploadProgress(Math.round((e.loaded / e.total) * 100)),
      });
      setUploadedKey(res.data.audio_key);
      setUploadProgress(100);
    } catch (e) {
      setSubmitError('Upload failed: ' + (e.response?.data?.error || e.message));
      setUploadFile(null);
    } finally {
      setUploading(false);
    }
  }

  async function handleTranscribe() {
    const audioKey = tab === 0 ? uploadedKey : recUploadedKey;
    if (!audioKey || !recordingName.trim()) return;
    setPhase('submitting');
    setSubmitError('');
    try {
      const res = await api.post('/api/transcribe-async', {
        audio_key: audioKey,
        recording_name: recordingName.trim(),
        tracker_node_id: trackerNodeId || null,
        tracker_node_title: trackerNodeTitle || null,
      });
      onJobStarted(res.data.job_id, recordingName.trim(), trackerNodeId, trackerNodeTitle);
      onClose();
    } catch (e) {
      setSubmitError('Failed to start transcription: ' + (e.response?.data?.error || e.message));
      setPhase('name');
    }
  }

  const readyToName = tab === 0 ? !!uploadedKey : !!recUploadedKey;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { bgcolor: 'var(--bg-surf)', color: 'var(--tx-hi)', borderRadius: 3, border: '1px solid var(--bd-lo)' } }}
    >
      <DialogTitle sx={{ pb: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
        <MicIcon sx={{ color: '#6366f1', fontSize: 22 }} />
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', flex: 1 }}>
          {phase === 'name' ? 'Name this recording' : 'Record Meeting'}
        </Typography>
        {trackerNodeTitle && (
          <Typography sx={{ fontSize: '0.72rem', color: '#818cf8', bgcolor: 'rgba(99,102,241,0.1)', px: 1, py: 0.3, borderRadius: 1 }}>
            {trackerNodeTitle}
          </Typography>
        )}
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--tx-dim)' }}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Recovery banner */}
        {recoveredSession && (
          <Alert severity="warning" sx={{ mb: 2, fontSize: '0.8rem' }}
            action={
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={recoverSession} sx={{ color: '#f59e0b', textTransform: 'none', fontWeight: 600 }}>Recover</Button>
                <Button size="small" onClick={() => { clearSessionMeta(); setRecoveredSession(null); }} sx={{ color: 'var(--tx-dim)', textTransform: 'none' }}>Discard</Button>
              </Stack>
            }
          >
            Incomplete recording found from {new Date(recoveredSession.started_at).toLocaleTimeString()}
          </Alert>
        )}

        {submitError && <Alert severity="error" sx={{ mb: 2, fontSize: '0.8rem' }}>{submitError}</Alert>}

        {/* ── Capture phase ── */}
        {phase === 'capture' && (
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, '& .MuiTab-root': { color: 'var(--tx-dim)', textTransform: 'none', minHeight: 36 }, '& .Mui-selected': { color: '#6366f1' }, '& .MuiTabs-indicator': { bgcolor: '#6366f1' } }}>
              <Tab label="Upload file" icon={<UploadFileIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab label="Live record" icon={<MicIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
            </Tabs>

            {/* Upload tab */}
            {tab === 0 && (
              <Box>
                <input ref={fileInputRef} type="file" accept="audio/*,.mp3,.m4a,.wav,.webm,.mp4,.ogg" style={{ display: 'none' }}
                  onChange={e => handleFileSelect(e.target.files?.[0])} />
                {!uploadFile ? (
                  <Box
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ border: '2px dashed var(--bd-lo)', borderRadius: 2, p: 4, textAlign: 'center', cursor: 'pointer',
                      '&:hover': { borderColor: '#6366f1', bgcolor: 'rgba(99,102,241,0.05)' }, transition: 'all 0.15s' }}
                  >
                    <UploadFileIcon sx={{ fontSize: 40, color: 'var(--tx-gh)', mb: 1 }} />
                    <Typography sx={{ color: 'var(--tx-lo)', fontSize: '0.85rem' }}>
                      Click to upload or drag & drop
                    </Typography>
                    <Typography sx={{ color: 'var(--tx-gh)', fontSize: '0.72rem', mt: 0.5 }}>
                      MP3, M4A, WAV, WEBM, MP4 supported
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ bgcolor: 'var(--bg-card)', borderRadius: 2, p: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} mb={uploading ? 1.5 : 0}>
                      <UploadFileIcon sx={{ color: uploading ? '#6366f1' : '#10b981', fontSize: 24 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: '0.82rem', color: 'var(--tx-md)', fontWeight: 500 }}>{uploadFile.name}</Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: 'var(--tx-gh)' }}>
                          {(uploadFile.size / 1024 / 1024).toFixed(1)} MB
                        </Typography>
                      </Box>
                      {uploadProgress === 100 && <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />}
                      {uploading && <CircularProgress size={18} sx={{ color: '#6366f1' }} />}
                      {!uploading && (
                        <IconButton size="small" onClick={() => { setUploadFile(null); setUploadedKey(null); setUploadProgress(0); }}
                          sx={{ color: 'var(--tx-dim)' }}><CloseIcon sx={{ fontSize: 15 }} /></IconButton>
                      )}
                    </Stack>
                    {uploading && <LinearProgress variant="determinate" value={uploadProgress} sx={{ borderRadius: 1, bgcolor: 'var(--bg-low)', '& .MuiLinearProgress-bar': { bgcolor: '#6366f1' } }} />}
                  </Box>
                )}
              </Box>
            )}

            {/* Live record tab */}
            {tab === 1 && (
              <Box sx={{ textAlign: 'center' }}>
                {/* Waveform canvas */}
                <Box sx={{ bgcolor: 'var(--bg-card)', borderRadius: 2, p: 1.5, mb: 2, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {recState === 'recording' ? (
                    <canvas ref={canvasRef} width={400} height={48}
                      style={{ width: '100%', height: 48, display: 'block' }} />
                  ) : recState === 'done' ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 22 }} />
                      <Typography sx={{ color: '#10b981', fontSize: '0.85rem', fontWeight: 600 }}>
                        Recording complete — {fmtDuration(duration)}
                      </Typography>
                    </Stack>
                  ) : recState === 'paused' ? (
                    <Typography sx={{ color: '#f59e0b', fontSize: '0.85rem' }}>Paused — {fmtDuration(duration)}</Typography>
                  ) : (
                    <Typography sx={{ color: 'var(--tx-gh)', fontSize: '0.82rem' }}>Press Record to start</Typography>
                  )}
                </Box>

                {/* Timer */}
                {(recState === 'recording' || recState === 'paused') && (
                  <Typography sx={{ fontSize: '1.8rem', fontWeight: 700, color: recState === 'recording' ? '#ef4444' : '#f59e0b', mb: 2, fontVariantNumeric: 'tabular-nums' }}>
                    {fmtDuration(duration)}
                  </Typography>
                )}

                {/* Controls */}
                <Stack direction="row" justifyContent="center" spacing={1.5}>
                  {recState === 'idle' && (
                    <Button variant="contained" startIcon={<MicIcon />} onClick={startRecording}
                      sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, textTransform: 'none', borderRadius: 2, px: 3 }}>
                      Record
                    </Button>
                  )}
                  {recState === 'recording' && (
                    <>
                      <Tooltip title="Pause"><IconButton onClick={pauseRecording} sx={{ bgcolor: 'var(--bg-card)', color: '#f59e0b', '&:hover': { bgcolor: 'var(--bg-hover)' } }}><PauseIcon /></IconButton></Tooltip>
                      <Tooltip title="Stop recording"><IconButton onClick={stopRecording} sx={{ bgcolor: '#ef4444', color: '#fff', '&:hover': { bgcolor: '#dc2626' } }}><StopIcon /></IconButton></Tooltip>
                    </>
                  )}
                  {recState === 'paused' && (
                    <>
                      <Tooltip title="Resume"><IconButton onClick={resumeRecording} sx={{ bgcolor: 'var(--bg-card)', color: '#6366f1', '&:hover': { bgcolor: 'var(--bg-hover)' } }}><PlayArrowIcon /></IconButton></Tooltip>
                      <Tooltip title="Stop recording"><IconButton onClick={stopRecording} sx={{ bgcolor: '#ef4444', color: '#fff', '&:hover': { bgcolor: '#dc2626' } }}><StopIcon /></IconButton></Tooltip>
                    </>
                  )}
                </Stack>
              </Box>
            )}

            {/* Continue button */}
            {tab === 0 && uploadedKey && (
              <Button fullWidth variant="contained" onClick={() => setPhase('name')} sx={{ mt: 2, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, textTransform: 'none', borderRadius: 2 }}>
                Continue →
              </Button>
            )}
            {tab === 1 && recState === 'done' && (
              <Button fullWidth variant="contained" onClick={uploadRecordedAudio} disabled={recUploading}
                sx={{ mt: 2, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, textTransform: 'none', borderRadius: 2 }}>
                {recUploading ? <><CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />Uploading…</> : 'Continue →'}
              </Button>
            )}
          </>
        )}

        {/* ── Naming phase ── */}
        {phase === 'name' && (
          <Box>
            <Typography sx={{ fontSize: '0.82rem', color: 'var(--tx-lo)', mb: 2 }}>
              Give this recording a name before transcribing.
            </Typography>
            <TextField
              fullWidth autoFocus
              label="Recording name"
              value={recordingName}
              onChange={e => setRecordingName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && readyToName && handleTranscribe()}
              helperText="e.g. 19mar2026-govtech-dataproject-meeting"
              sx={{
                '& .MuiOutlinedInput-root': { bgcolor: 'var(--bg-card)', color: 'var(--tx-md)', '& fieldset': { borderColor: 'var(--tx-gh)' }, '&:hover fieldset': { borderColor: '#6366f1' } },
                '& .MuiInputLabel-root': { color: 'var(--tx-dim)' },
                '& .MuiFormHelperText-root': { color: 'var(--tx-ft)' },
              }}
            />
          </Box>
        )}

        {/* ── Submitting phase ── */}
        {phase === 'submitting' && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress sx={{ color: '#6366f1', mb: 2 }} />
            <Typography sx={{ color: 'var(--tx-lo)', fontSize: '0.85rem' }}>Starting transcription job…</Typography>
          </Box>
        )}
      </DialogContent>

      {phase === 'name' && (
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setPhase('capture')} sx={{ color: 'var(--tx-dim)', textTransform: 'none' }}>Back</Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose} sx={{ color: 'var(--tx-dim)', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleTranscribe} disabled={!recordingName.trim()}
            sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, textTransform: 'none', borderRadius: 2, px: 2.5 }}>
            Transcribe &amp; Summarise
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
